/**
 * DashboardModals.tsx
 *
 * SETUP INSTRUCTIONS
 * ──────────────────
 * 1. Place this file at:  src/components/DashboardModals.tsx
 * 2. ⚠️  DELETE  src/components/DashboardModals.jsx  if it exists.
 *    (The .jsx file is what causes all the parse errors — webpack loads it
 *     instead of this .tsx file and chokes on TypeScript syntax.)
 * 3. In Dashboard.tsx add at the top:
 *      import { VoicemailsPage, TranscriptsPage, CallFlowsTab } from "./DashboardModals";
 * 4. In App.tsx keep: import { DashboardModals } from './components/DashboardModals';
 *    DashboardModals is exported as an empty component for backwards-compat.
 */

import React, { useState, useRef, useEffect } from "react";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Voicemail {
  id: string;
  callerNumber: string;
  duration: number;
  timestamp: any;
  recordingUrl?: string;
  transcript?: string;
  isRead: boolean;
  sentiment?: "positive" | "neutral" | "negative";
  callerName?: string;
}

export interface TranscriptMessage {
  role: "user" | "assistant";
  text: string;
  timestamp?: number;
  sentiment?: "positive" | "neutral" | "negative";
}

export interface CallTranscript {
  callId: string;
  phoneNumber: string;
  callerName?: string;
  duration: number;
  timestamp: any;
  messages: TranscriptMessage[];
  summary?: string;
  sentiment?: "positive" | "neutral" | "negative";
  recordingUrl?: string;
}

export type FlowNodeType =
  | "start"
  | "greet"
  | "menu"
  | "condition"
  | "transfer"
  | "voicemail"
  | "sms"
  | "end";

export interface FlowNode {
  id: string;
  type: FlowNodeType;
  label: string;
  x: number;
  y: number;
  config: Record<string, any>;
  outputs: Array<{ id: string; label: string; targetId?: string }>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDuration(s: number): string {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function fmtDate(ts: any): string {
  if (!ts) return "N/A";
  const d =
    typeof ts === "object" && "toDate" in ts ? ts.toDate() : new Date(ts);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function sentimentColor(s?: string): string {
  return s === "positive"
    ? "#10b981"
    : s === "negative"
      ? "#ef4444"
      : "#6b7280";
}

function sentimentIcon(s?: string): string {
  return s === "positive" ? "😊" : s === "negative" ? "😞" : "😐";
}

// ─── Voicemail Modal ─────────────────────────────────────────────────────────

interface VoicemailModalProps {
  voicemail: Voicemail;
  onClose: () => void;
  onMarkRead?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export const VoicemailModal: React.FC<VoicemailModalProps> = ({
  voicemail,
  onClose,
  onMarkRead,
  onDelete,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (onMarkRead && !voicemail.isRead) onMarkRead(voicemail.id);
  }, []); // eslint-disable-line

  function togglePlay() {
    const a = audioRef.current;
    if (!a) return;
    isPlaying ? a.pause() : a.play();
    setIsPlaying(!isPlaying);
  }

  function handleTimeUpdate() {
    const a = audioRef.current;
    if (!a) return;
    setCurrentTime(a.currentTime);
    setProgress((a.currentTime / a.duration) * 100 || 0);
  }

  function handleSeek(e: React.MouseEvent<HTMLDivElement>) {
    const a = audioRef.current;
    if (!a) return;
    const rect = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - rect.left) / rect.width) * a.duration;
  }

  const displayName = voicemail.callerName || voicemail.callerNumber;

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={S.modalHeader}>
          <div style={S.row}>
            <div style={S.avatar}>{displayName.charAt(0).toUpperCase()}</div>
            <div>
              <div style={S.modalTitle}>{displayName}</div>
              <div style={S.modalSub}>
                {voicemail.callerName && (
                  <span style={{ marginRight: 8 }}>
                    {voicemail.callerNumber}
                  </span>
                )}
                <span>{fmtDate(voicemail.timestamp)}</span>
                {!voicemail.isRead && <span style={S.newBadge}>NEW</span>}
              </div>
            </div>
          </div>
          <button style={S.closeBtn} onClick={onClose}>
            ×
          </button>
        </div>

        {/* Player */}
        <div style={S.section}>
          {voicemail.recordingUrl ? (
            <>
              <audio
                ref={audioRef}
                src={voicemail.recordingUrl}
                onTimeUpdate={handleTimeUpdate}
                onEnded={() => setIsPlaying(false)}
              />
              <div style={S.player}>
                <button style={S.playBtn} onClick={togglePlay}>
                  {isPlaying ? "⏸" : "▶"}
                </button>
                <div style={S.progressWrap} onClick={handleSeek}>
                  <div style={S.progressTrack}>
                    <div style={{ ...S.progressFill, width: `${progress}%` }} />
                  </div>
                </div>
                <span style={S.timeLabel}>
                  {fmtDuration(Math.floor(currentTime))} /{" "}
                  {fmtDuration(voicemail.duration)}
                </span>
                <a
                  href={voicemail.recordingUrl}
                  download
                  style={S.dlLink}
                  title="Download"
                >
                  ⬇
                </a>
              </div>
            </>
          ) : (
            <div style={S.emptyCenter}>
              <span style={{ fontSize: 32 }}>🎙️</span>
              <p style={{ color: "#6b7280", margin: "8px 0 0" }}>
                No recording available
              </p>
            </div>
          )}
        </div>

        {/* Transcript */}
        {voicemail.transcript && (
          <div style={S.section}>
            <div style={S.sectionLabel}>AUTO-TRANSCRIPT</div>
            <div style={S.transcriptBox}>{voicemail.transcript}</div>
          </div>
        )}

        {/* Meta */}
        <div style={S.metaRow}>
          <span style={S.chip}>⏱ {fmtDuration(voicemail.duration)}</span>
          {voicemail.sentiment && (
            <span
              style={{ ...S.chip, color: sentimentColor(voicemail.sentiment) }}
            >
              {sentimentIcon(voicemail.sentiment)} {voicemail.sentiment}
            </span>
          )}
        </div>

        {/* Actions */}
        <div style={S.modalFooter}>
          <button style={S.btnSecondary} onClick={onClose}>
            Close
          </button>
          {onDelete && (
            <button
              style={S.btnDanger}
              onClick={() => {
                onDelete(voicemail.id);
                onClose();
              }}
            >
              🗑 Delete
            </button>
          )}
          <button
            style={S.btnPrimary}
            onClick={() => window.open(`tel:${voicemail.callerNumber}`)}
          >
            📞 Call Back
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Voicemails Page ─────────────────────────────────────────────────────────

interface VoicemailsPageProps {
  voicemails: Voicemail[];
  onMarkRead: (id: string) => void;
  onDelete: (id: string) => void;
}

export const VoicemailsPage: React.FC<VoicemailsPageProps> = ({
  voicemails,
  onMarkRead,
  onDelete,
}) => {
  const [selected, setSelected] = useState<Voicemail | null>(null);
  const [filter, setFilter] = useState<"all" | "unread">("all");

  const unreadCount = voicemails.filter((v) => !v.isRead).length;
  const filtered =
    filter === "unread" ? voicemails.filter((v) => !v.isRead) : voicemails;

  return (
    <div style={S.page}>
      <div style={S.pageHeader}>
        <div>
          <h1 style={S.pageTitle}>Voicemails</h1>
          <p style={S.pageSubtitle}>
            {unreadCount} unread · {voicemails.length} total
          </p>
        </div>
        <div style={S.row}>
          <button
            style={filter === "all" ? S.filterActive : S.filterBtn}
            onClick={() => setFilter("all")}
          >
            All
          </button>
          <button
            style={filter === "unread" ? S.filterActive : S.filterBtn}
            onClick={() => setFilter("unread")}
          >
            Unread ({unreadCount})
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={S.emptyState}>
          <span style={{ fontSize: 48 }}>🎙️</span>
          <p style={{ color: "#9ca3af", marginTop: 12 }}>No voicemails found</p>
        </div>
      ) : (
        <div style={S.list}>
          {filtered.map((vm) => (
            <div
              key={vm.id}
              style={{
                ...S.listRow,
                background: vm.isRead ? "#fff" : "#f0f9ff",
                borderLeft: vm.isRead
                  ? "3px solid transparent"
                  : "3px solid #3b82f6",
              }}
              onClick={() => {
                setSelected(vm);
                if (!vm.isRead) onMarkRead(vm.id);
              }}
            >
              <div style={S.listAvatar}>
                {(vm.callerName || vm.callerNumber).charAt(0).toUpperCase()}
              </div>
              <div style={S.listInfo}>
                <div style={S.listName}>
                  {vm.callerName || vm.callerNumber}
                  {!vm.isRead && <span style={S.unreadDot} />}
                </div>
                {vm.callerName && (
                  <div style={S.listSub}>{vm.callerNumber}</div>
                )}
                {vm.transcript && (
                  <div style={S.listPreview}>{vm.transcript.slice(0, 80)}…</div>
                )}
              </div>
              <div style={S.listMeta}>
                <div style={S.listDuration}>{fmtDuration(vm.duration)}</div>
                <div style={S.listDate}>{fmtDate(vm.timestamp)}</div>
                {vm.sentiment && (
                  <div style={{ color: sentimentColor(vm.sentiment) }}>
                    {sentimentIcon(vm.sentiment)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <VoicemailModal
          voicemail={selected}
          onClose={() => setSelected(null)}
          onMarkRead={onMarkRead}
          onDelete={onDelete}
        />
      )}
    </div>
  );
};

// ─── Transcript Modal ─────────────────────────────────────────────────────────

interface TranscriptModalProps {
  transcript: CallTranscript;
  onClose: () => void;
}

export const TranscriptModal: React.FC<TranscriptModalProps> = ({
  transcript,
  onClose,
}) => {
  const [search, setSearch] = useState("");
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  function highlightText(text: string): React.ReactNode {
    if (!search) return text;
    const idx = text.toLowerCase().indexOf(search.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark style={S.highlight}>{text.slice(idx, idx + search.length)}</mark>
        {text.slice(idx + search.length)}
      </>
    );
  }

  const msgs = search
    ? transcript.messages.filter((m) =>
        m.text.toLowerCase().includes(search.toLowerCase()),
      )
    : transcript.messages;

  function downloadTxt() {
    const text = [
      "Call Transcript",
      `Date: ${fmtDate(transcript.timestamp)}`,
      `Duration: ${fmtDuration(transcript.duration)}`,
      `Phone: ${transcript.phoneNumber}`,
      `Summary: ${transcript.summary || "N/A"}`,
      "",
      ...transcript.messages.map(
        (m) => `[${m.role === "user" ? "Caller" : "AI"}]: ${m.text}`,
      ),
    ].join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transcript-${transcript.callId}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function copyAll() {
    const text = transcript.messages
      .map((m) => `${m.role === "user" ? "Caller" : "AI"}: ${m.text}`)
      .join("\n");
    navigator.clipboard.writeText(text);
  }

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.transcriptModal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={S.transcriptHeader}>
          <div style={S.row}>
            <div style={S.transcriptIcon}>📄</div>
            <div>
              <div style={S.modalTitle}>
                {transcript.callerName || transcript.phoneNumber}
              </div>
              <div style={S.modalSub}>
                {fmtDate(transcript.timestamp)} ·{" "}
                {fmtDuration(transcript.duration)}
                {transcript.sentiment && (
                  <span
                    style={{
                      marginLeft: 8,
                      color: sentimentColor(transcript.sentiment),
                    }}
                  >
                    {sentimentIcon(transcript.sentiment)} {transcript.sentiment}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div style={S.row}>
            <button style={S.iconBtn} onClick={copyAll} title="Copy">
              ⎘
            </button>
            <button style={S.iconBtn} onClick={downloadTxt} title="Download">
              ⬇
            </button>
            <button style={S.closeBtn} onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        {/* Summary */}
        {transcript.summary && (
          <div style={S.summaryBar}>
            <span style={S.summaryLabel}>SUMMARY</span>
            <span style={S.summaryText}>{transcript.summary}</span>
          </div>
        )}

        {/* Search */}
        <div style={S.searchBar}>
          <span>🔍</span>
          <input
            style={S.searchInput}
            placeholder="Search transcript…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <span style={{ color: "#6b7280", fontSize: 12 }}>
              {msgs.length} result{msgs.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Audio player */}
        {transcript.recordingUrl && (
          <div style={S.tinyPlayer}>
            <audio
              ref={audioRef}
              src={transcript.recordingUrl}
              onTimeUpdate={() => {
                const a = audioRef.current;
                if (a) setProgress((a.currentTime / a.duration) * 100 || 0);
              }}
              onEnded={() => setPlaying(false)}
            />
            <button
              style={S.playBtnSm}
              onClick={() => {
                const a = audioRef.current;
                if (!a) return;
                playing ? a.pause() : a.play();
                setPlaying(!playing);
              }}
            >
              {playing ? "⏸" : "▶"}
            </button>
            <div
              style={S.progressTrackSm}
              onClick={(e) => {
                const a = audioRef.current;
                if (!a) return;
                const rect = e.currentTarget.getBoundingClientRect();
                a.currentTime =
                  ((e.clientX - rect.left) / rect.width) * a.duration;
              }}
            >
              <div style={{ ...S.progressFillSm, width: `${progress}%` }} />
            </div>
            <span style={S.timeLabel}>{fmtDuration(transcript.duration)}</span>
          </div>
        )}

        {/* Messages */}
        <div style={S.messages}>
          {msgs.length === 0 ? (
            <div style={S.emptyCenter}>
              <p>No messages match your search</p>
            </div>
          ) : (
            msgs.map((msg, i) => {
              const isAI = msg.role === "assistant";
              return (
                <div
                  key={i}
                  style={{
                    ...S.bubbleRow,
                    justifyContent: isAI ? "flex-start" : "flex-end",
                  }}
                >
                  {isAI && <div style={S.botAvatar}>AI</div>}
                  <div
                    style={{
                      ...S.bubble,
                      background: isAI ? "#f3f4f6" : "#1d4ed8",
                      color: isAI ? "#111827" : "#fff",
                      borderBottomLeftRadius: isAI ? 4 : 16,
                      borderBottomRightRadius: isAI ? 16 : 4,
                    }}
                  >
                    <div style={S.bubbleRole}>
                      <span>{isAI ? "AI Agent" : "Caller"}</span>
                      {msg.timestamp != null && (
                        <span style={S.bubbleTime}>
                          {fmtDuration(msg.timestamp)}
                        </span>
                      )}
                    </div>
                    <div>{highlightText(msg.text)}</div>
                    {msg.sentiment && (
                      <div
                        style={{
                          ...S.bubbleSentiment,
                          color: isAI
                            ? sentimentColor(msg.sentiment)
                            : "rgba(255,255,255,0.7)",
                        }}
                      >
                        {sentimentIcon(msg.sentiment)}
                      </div>
                    )}
                  </div>
                  {!isAI && (
                    <div style={S.userAvatar}>
                      {transcript.phoneNumber.slice(-2)}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={S.modalFooter}>
          <button style={S.btnSecondary} onClick={onClose}>
            Close
          </button>
          <button style={S.btnPrimary} onClick={downloadTxt}>
            ⬇ Download
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Transcripts Page ─────────────────────────────────────────────────────────

interface TranscriptsPageProps {
  transcripts: CallTranscript[];
}

export const TranscriptsPage: React.FC<TranscriptsPageProps> = ({
  transcripts,
}) => {
  const [selected, setSelected] = useState<CallTranscript | null>(null);
  const [search, setSearch] = useState("");

  const filtered = search
    ? transcripts.filter(
        (t) =>
          t.phoneNumber.includes(search) ||
          (t.callerName || "").toLowerCase().includes(search.toLowerCase()) ||
          (t.summary || "").toLowerCase().includes(search.toLowerCase()),
      )
    : transcripts;

  return (
    <div style={S.page}>
      <div style={S.pageHeader}>
        <div>
          <h1 style={S.pageTitle}>Transcripts</h1>
          <p style={S.pageSubtitle}>{transcripts.length} call transcripts</p>
        </div>
        <div style={S.searchBarOuter}>
          <span>🔍</span>
          <input
            style={S.searchInput}
            placeholder="Search transcripts…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={S.emptyState}>
          <span style={{ fontSize: 48 }}>📄</span>
          <p style={{ color: "#9ca3af", marginTop: 12 }}>
            No transcripts found
          </p>
        </div>
      ) : (
        <div style={S.list}>
          {filtered.map((t) => (
            <div
              key={t.callId}
              style={S.listRow}
              onClick={() => setSelected(t)}
            >
              <div style={S.transcriptRowIcon}>📄</div>
              <div style={S.listInfo}>
                <div style={S.listName}>{t.callerName || t.phoneNumber}</div>
                {t.callerName && <div style={S.listSub}>{t.phoneNumber}</div>}
                {t.summary && <div style={S.listPreview}>{t.summary}</div>}
                <div style={S.msgCount}>
                  {t.messages.length} message
                  {t.messages.length !== 1 ? "s" : ""}
                </div>
              </div>
              <div style={S.listMeta}>
                <div style={S.listDuration}>{fmtDuration(t.duration)}</div>
                <div style={S.listDate}>{fmtDate(t.timestamp)}</div>
                {t.sentiment && (
                  <div style={{ color: sentimentColor(t.sentiment) }}>
                    {sentimentIcon(t.sentiment)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <TranscriptModal
          transcript={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

// ─── Call Flows Tab ───────────────────────────────────────────────────────────

const NODE_META: Record<
  FlowNodeType,
  { bg: string; border: string; icon: string }
> = {
  start: { bg: "#dcfce7", border: "#16a34a", icon: "▶" },
  greet: { bg: "#dbeafe", border: "#2563eb", icon: "👋" },
  menu: { bg: "#fef3c7", border: "#d97706", icon: "📋" },
  condition: { bg: "#f3e8ff", border: "#9333ea", icon: "◇" },
  transfer: { bg: "#fce7f3", border: "#db2777", icon: "📞" },
  voicemail: { bg: "#f0fdf4", border: "#15803d", icon: "🎙️" },
  sms: { bg: "#eff6ff", border: "#1d4ed8", icon: "💬" },
  end: { bg: "#fee2e2", border: "#dc2626", icon: "⏹" },
};

const DEFAULT_FLOW: FlowNode[] = [
  {
    id: "1",
    type: "start",
    label: "Incoming Call",
    x: 60,
    y: 60,
    config: {},
    outputs: [{ id: "o1", label: "Answer", targetId: "2" }],
  },
  {
    id: "2",
    type: "greet",
    label: "Greeting",
    x: 280,
    y: 60,
    config: { message: "Hello! Thank you for calling. How can I help?" },
    outputs: [{ id: "o2", label: "Continue", targetId: "3" }],
  },
  {
    id: "3",
    type: "menu",
    label: "Main Menu",
    x: 500,
    y: 60,
    config: { prompt: "Press 1 for Sales, 2 for Support, 3 for Voicemail" },
    outputs: [
      { id: "o3a", label: "Sales", targetId: "4" },
      { id: "o3b", label: "Support", targetId: "4" },
      { id: "o3c", label: "Voicemail", targetId: "5" },
    ],
  },
  {
    id: "4",
    type: "transfer",
    label: "Transfer to Agent",
    x: 720,
    y: 60,
    config: { number: "" },
    outputs: [{ id: "o4", label: "End", targetId: "6" }],
  },
  {
    id: "5",
    type: "voicemail",
    label: "Leave Voicemail",
    x: 720,
    y: 200,
    config: { message: "Please leave a message after the beep." },
    outputs: [{ id: "o5", label: "End", targetId: "6" }],
  },
  {
    id: "6",
    type: "end",
    label: "End Call",
    x: 940,
    y: 60,
    config: {},
    outputs: [],
  },
];

const NODE_W = 164;
const NODE_H = 72;
const TOOLBOX_TYPES: FlowNodeType[] = [
  "greet",
  "menu",
  "condition",
  "transfer",
  "voicemail",
  "sms",
  "end",
];

interface CallFlowsTabProps {
  onSave?: (nodes: FlowNode[]) => void;
  initialNodes?: FlowNode[];
}

export const CallFlowsTab: React.FC<CallFlowsTabProps> = ({
  onSave,
  initialNodes,
}) => {
  const [nodes, setNodes] = useState<FlowNode[]>(initialNodes || DEFAULT_FLOW);
  const [selected, setSelected] = useState<FlowNode | null>(null);
  const [dragging, setDragging] = useState<{
    id: string;
    ox: number;
    oy: number;
  } | null>(null);
  const [saved, setSaved] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);

  function addNode(type: FlowNodeType) {
    const id = String(Date.now());
    const node: FlowNode = {
      id,
      type,
      label: type.charAt(0).toUpperCase() + type.slice(1),
      x: 120 + Math.random() * 180,
      y: 260 + Math.random() * 80,
      config: {},
      outputs: type === "end" ? [] : [{ id: `${id}-o1`, label: "Next" }],
    };
    setNodes((prev) => [...prev, node]);
  }

  function deleteNode(id: string) {
    if (id === "1") return;
    setNodes((prev) => prev.filter((n) => n.id !== id));
    if (selected?.id === id) setSelected(null);
  }

  function patchConfig(id: string, patch: Record<string, any>) {
    setNodes((prev) =>
      prev.map((n) =>
        n.id === id ? { ...n, config: { ...n.config, ...patch } } : n,
      ),
    );
    setSelected((s) =>
      s?.id === id ? { ...s, config: { ...s.config, ...patch } } : s,
    );
  }

  function patchLabel(id: string, label: string) {
    setNodes((prev) => prev.map((n) => (n.id === id ? { ...n, label } : n)));
    setSelected((s) => (s?.id === id ? { ...s, label } : s));
  }

  function wireOutput(fromId: string, outId: string, toId: string) {
    setNodes((prev) =>
      prev.map((n) =>
        n.id === fromId
          ? {
              ...n,
              outputs: n.outputs.map((o) =>
                o.id === outId ? { ...o, targetId: toId } : o,
              ),
            }
          : n,
      ),
    );
  }

  function onNodeMouseDown(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    const node = nodes.find((n) => n.id === id);
    if (!node || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    setDragging({
      id,
      ox: e.clientX - rect.left - node.x,
      oy: e.clientY - rect.top - node.y,
    });
    setSelected(node);
  }

  function onCanvasMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (!dragging || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    setNodes((prev) =>
      prev.map((n) =>
        n.id === dragging.id
          ? {
              ...n,
              x: Math.max(0, e.clientX - rect.left - dragging.ox),
              y: Math.max(0, e.clientY - rect.top - dragging.oy),
            }
          : n,
      ),
    );
  }

  function save() {
    if (onSave) onSave(nodes);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  // Build arrows
  const arrows: Array<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    label: string;
  }> = [];
  nodes.forEach((node) => {
    const total = node.outputs.length;
    node.outputs.forEach((out, oi) => {
      if (!out.targetId) return;
      const target = nodes.find((n) => n.id === out.targetId);
      if (!target) return;
      const yOff = total > 1 ? (oi / (total - 1) - 0.5) * NODE_H * 0.6 : 0;
      arrows.push({
        x1: node.x + NODE_W,
        y1: node.y + NODE_H / 2 + yOff,
        x2: target.x,
        y2: target.y + NODE_H / 2,
        label: out.label,
      });
    });
  });

  return (
    <div style={S.page}>
      <div style={S.pageHeader}>
        <div>
          <h1 style={S.pageTitle}>Call Flows</h1>
          <p style={S.pageSubtitle}>
            Drag nodes, connect outputs, configure each step
          </p>
        </div>
        <button style={saved ? S.btnSaved : S.btnPrimary} onClick={save}>
          {saved ? "✓ Saved!" : "💾 Save Flow"}
        </button>
      </div>

      <div style={S.flowLayout}>
        {/* Toolbox */}
        <div style={S.toolbox}>
          <div style={S.toolboxTitle}>ADD NODE</div>
          {TOOLBOX_TYPES.map((type) => {
            const m = NODE_META[type];
            return (
              <button
                key={type}
                style={{
                  ...S.toolboxBtn,
                  background: m.bg,
                  borderColor: m.border,
                }}
                onClick={() => addNode(type)}
              >
                <span>{m.icon}</span>
                <span style={S.toolboxLabel}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </span>
              </button>
            );
          })}

          <div style={{ marginTop: 20 }}>
            <div style={S.toolboxTitle}>LEGEND</div>
            {(
              Object.entries(NODE_META) as Array<
                [FlowNodeType, (typeof NODE_META)[FlowNodeType]]
              >
            ).map(([t, m]) => (
              <div key={t} style={S.legendRow}>
                <div style={{ ...S.legendDot, background: m.border }} />
                <span style={S.legendLabel}>
                  {m.icon} {t}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Canvas */}
        <div
          ref={canvasRef}
          style={S.canvas}
          onMouseMove={onCanvasMouseMove}
          onMouseUp={() => setDragging(null)}
          onMouseLeave={() => setDragging(null)}
          onClick={() => setSelected(null)}
        >
          {/* SVG arrows */}
          <svg style={S.svgLayer}>
            <defs>
              <marker
                id="ah"
                markerWidth="10"
                markerHeight="7"
                refX="10"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8" />
              </marker>
            </defs>
            {arrows.map((a, i) => {
              const cx = (a.x1 + a.x2) / 2;
              return (
                <g key={i}>
                  <path
                    d={`M ${a.x1} ${a.y1} C ${cx} ${a.y1}, ${cx} ${a.y2}, ${a.x2} ${a.y2}`}
                    fill="none"
                    stroke="#94a3b8"
                    strokeWidth={2}
                    markerEnd="url(#ah)"
                  />
                  <text
                    x={cx}
                    y={(a.y1 + a.y2) / 2 - 6}
                    textAnchor="middle"
                    fontSize={10}
                    fill="#6b7280"
                    fontFamily="monospace"
                  >
                    {a.label}
                  </text>
                </g>
              );
            })}
          </svg>

          {/* Nodes */}
          {nodes.map((node) => {
            const m = NODE_META[node.type];
            const isSel = selected?.id === node.id;
            return (
              <div
                key={node.id}
                style={{
                  ...S.node,
                  left: node.x,
                  top: node.y,
                  width: NODE_W,
                  minHeight: NODE_H,
                  background: m.bg,
                  borderColor: isSel ? "#111827" : m.border,
                  boxShadow: isSel
                    ? "0 0 0 3px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.12)"
                    : "0 2px 8px rgba(0,0,0,0.08)",
                  cursor: dragging?.id === node.id ? "grabbing" : "grab",
                  zIndex: isSel ? 10 : 1,
                }}
                onMouseDown={(e) => onNodeMouseDown(e, node.id)}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelected(node);
                }}
              >
                <div style={S.nodeHead}>
                  <span style={{ fontSize: 13 }}>{m.icon}</span>
                  <span style={{ ...S.nodeLabel, color: m.border }}>
                    {node.label}
                  </span>
                  {node.id !== "1" && (
                    <button
                      style={S.nodeDelBtn}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNode(node.id);
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
                {node.config.message && (
                  <div style={S.nodePreview}>
                    {String(node.config.message).slice(0, 36)}…
                  </div>
                )}
                {node.config.number && (
                  <div style={S.nodePreview}>→ {node.config.number}</div>
                )}
                <div style={S.ports}>
                  {node.outputs.map((out) => (
                    <div key={out.id} style={S.portRow}>
                      <span style={S.portLabel}>{out.label}</span>
                      <div
                        style={{
                          ...S.portDot,
                          background: out.targetId ? m.border : "#d1d5db",
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {nodes.length === 0 && (
            <div style={S.canvasHint}>
              Add nodes from the panel to build your flow
            </div>
          )}
        </div>

        {/* Config panel */}
        {selected && (
          <div style={S.configPanel}>
            <div style={S.configTitle}>
              {NODE_META[selected.type].icon}{" "}
              {selected.type.charAt(0).toUpperCase() + selected.type.slice(1)}{" "}
              Node
            </div>

            <div style={S.cfgGroup}>
              <label style={S.cfgLabel}>Display Label</label>
              <input
                style={S.cfgInput}
                value={selected.label}
                onChange={(e) => patchLabel(selected.id, e.target.value)}
              />
            </div>

            {(selected.type === "greet" || selected.type === "voicemail") && (
              <div style={S.cfgGroup}>
                <label style={S.cfgLabel}>Spoken Message</label>
                <textarea
                  style={S.cfgTextarea}
                  rows={4}
                  value={selected.config.message || ""}
                  onChange={(e) =>
                    patchConfig(selected.id, { message: e.target.value })
                  }
                  placeholder="Message to speak to caller…"
                />
              </div>
            )}

            {selected.type === "menu" && (
              <div style={S.cfgGroup}>
                <label style={S.cfgLabel}>Menu Prompt</label>
                <textarea
                  style={S.cfgTextarea}
                  rows={4}
                  value={selected.config.prompt || ""}
                  onChange={(e) =>
                    patchConfig(selected.id, { prompt: e.target.value })
                  }
                  placeholder="Describe the menu options…"
                />
              </div>
            )}

            {selected.type === "transfer" && (
              <div style={S.cfgGroup}>
                <label style={S.cfgLabel}>Transfer To Number</label>
                <input
                  style={S.cfgInput}
                  type="tel"
                  value={selected.config.number || ""}
                  onChange={(e) =>
                    patchConfig(selected.id, { number: e.target.value })
                  }
                  placeholder="+27821234567"
                />
              </div>
            )}

            {selected.type === "sms" && (
              <div style={S.cfgGroup}>
                <label style={S.cfgLabel}>SMS Message</label>
                <textarea
                  style={S.cfgTextarea}
                  rows={3}
                  value={selected.config.message || ""}
                  onChange={(e) =>
                    patchConfig(selected.id, { message: e.target.value })
                  }
                  placeholder="SMS text to send…"
                />
              </div>
            )}

            {selected.type === "condition" && (
              <div style={S.cfgGroup}>
                <label style={S.cfgLabel}>Condition Type</label>
                <select
                  style={S.cfgSelect}
                  value={selected.config.condition || "intent"}
                  onChange={(e) =>
                    patchConfig(selected.id, { condition: e.target.value })
                  }
                >
                  <option value="intent">Caller Intent</option>
                  <option value="time">Business Hours</option>
                  <option value="repeat">Repeat Caller</option>
                  <option value="sentiment">Caller Sentiment</option>
                </select>
              </div>
            )}

            {selected.outputs.length > 0 && (
              <div style={S.cfgGroup}>
                <label style={S.cfgLabel}>Connect Outputs →</label>
                {selected.outputs.map((out) => (
                  <div key={out.id} style={S.outputRow}>
                    <span style={S.outputLabel}>{out.label}</span>
                    <select
                      style={S.outputSelect}
                      value={out.targetId || ""}
                      onChange={(e) =>
                        wireOutput(selected.id, out.id, e.target.value)
                      }
                    >
                      <option value="">-- unconnected --</option>
                      {nodes
                        .filter((n) => n.id !== selected.id)
                        .map((n) => (
                          <option key={n.id} value={n.id}>
                            {n.label}
                          </option>
                        ))}
                    </select>
                  </div>
                ))}
              </div>
            )}

            {selected.id !== "1" && (
              <button
                style={{ ...S.btnDanger, marginTop: 8, width: "100%" }}
                onClick={() => deleteNode(selected.id)}
              >
                🗑 Delete Node
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Backwards-compat default export ────────────────────────────────────────
// App.tsx uses: import { DashboardModals } from './components/DashboardModals'
// and renders: <DashboardModals />  — kept as empty component so it compiles.
export const DashboardModals: React.FC = () => null;

// ─── Styles ───────────────────────────────────────────────────────────────────

const S: Record<string, React.CSSProperties> = {
  // layout
  page: { padding: "24px 32px", boxSizing: "border-box" },
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 24,
    flexWrap: "wrap",
    gap: 12,
  },
  pageTitle: { fontSize: 24, fontWeight: 700, color: "#111827", margin: 0 },
  pageSubtitle: { fontSize: 14, color: "#6b7280", margin: "4px 0 0" },
  row: { display: "flex", alignItems: "center", gap: 10 },

  // overlay / modals
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    backdropFilter: "blur(2px)",
  },
  modal: {
    background: "#fff",
    borderRadius: 16,
    width: "min(560px, 96vw)",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
  },
  transcriptModal: {
    background: "#fff",
    borderRadius: 16,
    width: "min(760px, 96vw)",
    maxHeight: "92vh",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px 16px",
    borderBottom: "1px solid #f3f4f6",
  },
  transcriptHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px 16px",
    borderBottom: "1px solid #f3f4f6",
    position: "sticky",
    top: 0,
    background: "#fff",
    zIndex: 10,
  },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "#111827" },
  modalSub: { fontSize: 13, color: "#6b7280", marginTop: 2 },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 24,
    cursor: "pointer",
    color: "#6b7280",
    padding: "0 4px",
    lineHeight: 1,
  },
  iconBtn: {
    background: "#f3f4f6",
    border: "none",
    borderRadius: 8,
    width: 32,
    height: 32,
    cursor: "pointer",
    fontSize: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    padding: "16px 24px",
    borderTop: "1px solid #f3f4f6",
  },
  section: { padding: "16px 24px", borderBottom: "1px solid #f3f4f6" },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#9ca3af",
    letterSpacing: "0.08em",
    marginBottom: 8,
    textTransform: "uppercase",
  },

  // avatar
  avatar: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#3b82f6,#1d4ed8)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 18,
    flexShrink: 0,
  },
  transcriptIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: "#eff6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    flexShrink: 0,
  },

  // player
  player: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "#f8fafc",
    borderRadius: 12,
    padding: "12px 16px",
  },
  playBtn: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#3b82f6,#1d4ed8)",
    border: "none",
    color: "#fff",
    fontSize: 16,
    cursor: "pointer",
    flexShrink: 0,
  },
  playBtnSm: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "#1d4ed8",
    border: "none",
    color: "#fff",
    fontSize: 12,
    cursor: "pointer",
    flexShrink: 0,
  },
  progressWrap: { flex: 1, cursor: "pointer", padding: "8px 0" },
  progressTrack: {
    height: 6,
    background: "#e2e8f0",
    borderRadius: 3,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    background: "linear-gradient(90deg,#3b82f6,#1d4ed8)",
    borderRadius: 3,
    transition: "width 0.1s",
  },
  progressTrackSm: {
    flex: 1,
    height: 4,
    background: "#e2e8f0",
    borderRadius: 2,
    cursor: "pointer",
  },
  progressFillSm: { height: "100%", background: "#1d4ed8", borderRadius: 2 },
  tinyPlayer: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 24px",
    borderBottom: "1px solid #f3f4f6",
    background: "#f8fafc",
  },
  timeLabel: { fontSize: 12, color: "#6b7280", flexShrink: 0 },
  dlLink: {
    color: "#3b82f6",
    textDecoration: "none",
    fontSize: 16,
    flexShrink: 0,
  },
  emptyCenter: { textAlign: "center", padding: "24px 0" },

  // transcript box
  transcriptBox: {
    background: "#f8fafc",
    borderRadius: 10,
    padding: "12px 14px",
    fontSize: 14,
    color: "#374151",
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
  },
  metaRow: { display: "flex", gap: 10, padding: "12px 24px", flexWrap: "wrap" },
  chip: {
    fontSize: 12,
    background: "#f3f4f6",
    borderRadius: 20,
    padding: "4px 12px",
    color: "#374151",
    fontWeight: 500,
  },
  newBadge: {
    marginLeft: 8,
    background: "#3b82f6",
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
    borderRadius: 4,
    padding: "2px 6px",
    letterSpacing: "0.06em",
  },

  // summary
  summaryBar: {
    background: "#f0fdf4",
    borderBottom: "1px solid #dcfce7",
    padding: "10px 24px",
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
  },
  summaryLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#16a34a",
    letterSpacing: "0.1em",
    marginTop: 2,
    flexShrink: 0,
  },
  summaryText: { fontSize: 13, color: "#374151", lineHeight: 1.5 },

  // search
  searchBar: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 24px",
    borderBottom: "1px solid #f3f4f6",
    background: "#fafafa",
  },
  searchBarOuter: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#f3f4f6",
    borderRadius: 10,
    padding: "8px 14px",
  },
  searchInput: {
    flex: 1,
    background: "none",
    border: "none",
    outline: "none",
    fontSize: 14,
    color: "#374151",
    minWidth: 160,
  },

  // messages
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: "16px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    minHeight: 240,
    maxHeight: 440,
  },
  bubbleRow: { display: "flex", alignItems: "flex-end", gap: 8 },
  bubble: {
    maxWidth: "72%",
    borderRadius: 16,
    padding: "10px 14px",
    fontSize: 14,
    lineHeight: 1.55,
  },
  bubbleRole: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.06em",
    marginBottom: 4,
    opacity: 0.6,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    textTransform: "uppercase",
  },
  bubbleTime: { fontWeight: 400, marginLeft: 8 },
  bubbleSentiment: { fontSize: 11, marginTop: 4, textAlign: "right" },
  botAvatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "#1d4ed8",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    fontWeight: 700,
    flexShrink: 0,
  },
  userAvatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "#374151",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    fontWeight: 700,
    flexShrink: 0,
  },
  highlight: {
    background: "#fef08a",
    color: "#111827",
    borderRadius: 3,
    padding: "0 2px",
  },

  // list
  list: {
    display: "flex",
    flexDirection: "column",
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #e5e7eb",
    background: "#fff",
  },
  listRow: {
    display: "flex",
    alignItems: "center",
    padding: "14px 16px",
    cursor: "pointer",
    gap: 14,
    borderBottom: "1px solid #f3f4f6",
    background: "#fff",
  },
  listAvatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#3b82f6,#1d4ed8)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 15,
    flexShrink: 0,
  },
  listInfo: { flex: 1, minWidth: 0 },
  listName: {
    fontWeight: 600,
    color: "#111827",
    fontSize: 14,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  listSub: { fontSize: 12, color: "#6b7280", marginTop: 1 },
  listPreview: {
    fontSize: 12,
    color: "#9ca3af",
    marginTop: 2,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  listMeta: {
    textAlign: "right",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 2,
  },
  listDuration: { fontSize: 13, fontWeight: 600, color: "#374151" },
  listDate: { fontSize: 11, color: "#9ca3af" },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#3b82f6",
    display: "inline-block",
    flexShrink: 0,
  },
  transcriptRowIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: "#eff6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    flexShrink: 0,
  },
  msgCount: { fontSize: 11, color: "#3b82f6", marginTop: 2, fontWeight: 500 },

  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 64,
    color: "#9ca3af",
    textAlign: "center",
  },
  filterBtn: {
    background: "#f3f4f6",
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    fontSize: 13,
    cursor: "pointer",
    color: "#6b7280",
    fontWeight: 500,
  },
  filterActive: {
    background: "#1d4ed8",
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    fontSize: 13,
    cursor: "pointer",
    color: "#fff",
    fontWeight: 600,
  },

  // call flows
  flowLayout: {
    display: "flex",
    height: "calc(100vh - 220px)",
    minHeight: 520,
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    overflow: "hidden",
    background: "#fff",
  },
  toolbox: {
    width: 164,
    borderRight: "1px solid #e5e7eb",
    padding: "16px 12px",
    overflowY: "auto",
    background: "#fafafa",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: 5,
  },
  toolboxTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: "#9ca3af",
    letterSpacing: "0.1em",
    marginBottom: 4,
    marginTop: 6,
  },
  toolboxBtn: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "7px 10px",
    borderRadius: 8,
    border: "1.5px solid",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 500,
  },
  toolboxLabel: { color: "#374151" },
  legendRow: { display: "flex", alignItems: "center", gap: 6, marginBottom: 4 },
  legendDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  legendLabel: { fontSize: 11, color: "#6b7280" },
  canvas: {
    flex: 1,
    position: "relative",
    overflow: "auto",
    background:
      "radial-gradient(circle, #e2e8f0 1px, transparent 1px) 0 0 / 24px 24px",
    userSelect: "none",
  },
  svgLayer: {
    position: "absolute",
    inset: 0,
    width: "200%",
    height: "200%",
    pointerEvents: "none",
    overflow: "visible",
  },
  node: {
    position: "absolute",
    borderRadius: 12,
    border: "2px solid",
    padding: "10px 12px",
    boxSizing: "border-box",
    transition: "box-shadow 0.15s",
  },
  nodeHead: { display: "flex", alignItems: "center", gap: 5, marginBottom: 3 },
  nodeLabel: {
    fontSize: 11,
    fontWeight: 700,
    flex: 1,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  nodeDelBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#ef4444",
    fontSize: 15,
    lineHeight: 1,
    padding: 0,
    flexShrink: 0,
  },
  nodePreview: {
    fontSize: 9,
    color: "#6b7280",
    marginBottom: 4,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  ports: { display: "flex", flexDirection: "column", gap: 2, marginTop: 3 },
  portRow: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 4,
  },
  portLabel: {
    fontSize: 9,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  portDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    border: "2px solid #fff",
    transition: "background 0.2s",
  },
  canvasHint: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%,-50%)",
    color: "#9ca3af",
    fontSize: 14,
    textAlign: "center",
    pointerEvents: "none",
  },
  configPanel: {
    width: 240,
    borderLeft: "1px solid #e5e7eb",
    padding: "16px 14px",
    overflowY: "auto",
    background: "#fafafa",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  configTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#111827",
    paddingBottom: 8,
    borderBottom: "1px solid #e5e7eb",
  },
  cfgGroup: { display: "flex", flexDirection: "column", gap: 4 },
  cfgLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#9ca3af",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  cfgInput: {
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 13,
    color: "#111827",
    outline: "none",
    background: "#fff",
  },
  cfgTextarea: {
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 13,
    color: "#111827",
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
    background: "#fff",
  },
  cfgSelect: {
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 13,
    color: "#111827",
    outline: "none",
    background: "#fff",
    width: "100%",
  },
  outputRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 4 },
  outputLabel: { fontSize: 11, color: "#374151", flexShrink: 0, minWidth: 56 },
  outputSelect: {
    flex: 1,
    border: "1px solid #d1d5db",
    borderRadius: 6,
    padding: "4px 6px",
    fontSize: 11,
    color: "#374151",
    outline: "none",
    background: "#fff",
  },

  // buttons
  btnPrimary: {
    background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnSecondary: {
    background: "#f3f4f6",
    color: "#374151",
    border: "none",
    borderRadius: 10,
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
  },
  btnDanger: {
    background: "#fee2e2",
    color: "#dc2626",
    border: "none",
    borderRadius: 10,
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnSaved: {
    background: "#dcfce7",
    color: "#16a34a",
    border: "none",
    borderRadius: 10,
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "default",
  },
};
